import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { useFormikContext } from 'formik';
import { FormPart } from '@modules/shared/components/FormPart';
import {
  requestCreateContactComment,
} from '@modules/ContactManager/components/Sidebar/CommentsTabs/queries';
import { COMMENT_TYPES } from '@modules/ContactManager/components/Sidebar/CommentsTabs/types';
import { statesList } from '@shared/constants/statesList';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { TextInputField } from '@shared/fields/TextInputField';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { openPdf, queryParams } from '@shared/commonFunctions';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { CONFIRMATION_RESULT, Roles } from '@shared/constants/constants';
import { Modal } from '@shared/components/modals/Modal';
import { colorTheme } from '@styles/muiTheme';
import { useAppDispatch } from '@store/hooks';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { requestContactDetails } from '../requests';
import {
  ADDFileType,
  IAdD123Response,
  IContactDetailsResponse,
  IDataResponse,
  IFormAsset,
  IXmlDataResponse,
} from '../interfaces';
import { IVehicleTitleInfoData, requestPullVehicleTitleInfo } from '../../../shared';

const ASSET_REGISTRATION_PREFIX = 'assetRegistration';

const BRAND_PRESENT = 'Y';

const TITLE_BRAND = 'Brand Title';
const TITLE_ADD123 = 'ADD123 Title information pulled:';
const TITLE_ODOMETER_READING = 'Odometer Reading';
const TITLE_ODOMETER_DISCREPANCY = 'Odometer Discrepancy:';
const TITLE_DISCREPANCY = 'Title Discrepancy';

const ERROR_MESSAGE_MILEAGE = 'Verify Mileage Entered';
const ERROR_MESSAGE_PULL_MILEAGE = 'Unable to Pull Mileage';
const ERROR_MESSAGE_UNABLE_TO_PROCEED = 'Unable to Proceed';

const MESSAGE_NO_DISCREPANCIES = 'No Discrepancies found';
const MESSAGE_REGISTRATION_STATE_DISCREPANCY = 'Registration State Does Not Match Contact State';

const COMMENT_REGISTRATION_STATE_DISCREPANCY = `${TITLE_ADD123} - ${MESSAGE_REGISTRATION_STATE_DISCREPANCY}`;

export const TitleForm = () => {
  const params = queryParams();
  const dispatch = useAppDispatch();
  const { userId } = useUserAccount();
  const { setFieldValue, values } = useFormikContext<IFormAsset>();
  const { isAdmin, userHasRoles } = useUserAccount();

  const [discrepancies, setDiscrepancies] = useState(false);
  const [loading, setLoading] = useState(false);
  const [IsTitleInfoPulled, setIsTitleInfoPulled] = useState(false);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [openLpLeadOverrideModal, setOpenLpLeadOverrideModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [stateMismatchMessage, setStateMismatchMessage] = useState<string[]>([]);

  const formConfig = useMemo(
    () => [
      {
        width: '100%',
        name: `${ASSET_REGISTRATION_PREFIX}.titleNumber`,
        label: 'Title #',
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${ASSET_REGISTRATION_PREFIX}.titleState`,
        label: 'Title State',
        options: statesList,
        component: SelectAutocompleteField,
      },

      {
        width: '100%',
        name: 'isBrandedTitle',
        label: 'Branded Title',
        component: CheckboxField,
      },
    ],
    [userId, values]
  );

  const isBrandedFields = [
    {
      width: '100%',
      name: 'assetBrand.brandedStateList',
      label: 'Brand State',
      options: statesList,
      component: SelectAutocompleteField,
      multiple: true,
      limitTags: 1
    },
    {
      width: '100%',
      name: 'assetBrand.brandName',
      label: 'Brand Type',
    },
    {
      width: '100%',
      name: 'assetBrand.brandDescription',
      label: 'Brand Description',
      multiline: true,
      component: TextInputField,
      maxRows: 5,
      inputProps: {
        style: { padding: '0px', fontWeight: '600', color: `${colorTheme.palette.primary.main}` },
      },
    },
  ];

  const button = {
    width: '100%',
    size: 'medium',
    component: () => (
      <Button
        variant="contained"
        color="info"
        onClick={() => pullTitleInfo(CONFIRMATION_RESULT.PENDING)}
      >
        Pull Title Info
      </Button>
    ),
  };

  const formConfigFields = values?.isBrandedTitle ? [...formConfig, ...isBrandedFields, button] : [...formConfig, button];

  const hasOverrideRoles = useMemo(() => isAdmin || userHasRoles([Roles.LP_Lead, Roles.LP_Manager]), [isAdmin, userHasRoles]);

  const handleDiscrepancies = useCallback(() => {
    if (!discrepancies) {
      const comment = `${TITLE_ADD123} ${MESSAGE_NO_DISCREPANCIES}`;
      postContactComment(comment);
    }
  }, [discrepancies, IsTitleInfoPulled]);

  useEffect(() => {
    if (IsTitleInfoPulled) {
      handleDiscrepancies();
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
    }
  }, [IsTitleInfoPulled]);

  const handleDataResponse = useCallback(
    async (data: IDataResponse, postData: IVehicleTitleInfoData) => {
      const { accessTokenDoesNotExists, authorizeUrl, isError, errorDescription, fileInBytes, transactionId } = data;

      const { vin, id } = values;

      if (accessTokenDoesNotExists) {
        handleAccessTokenError(authorizeUrl);
        return;
      }

      if (isError) {
        Notistack.generalError(errorDescription);
        return;
      }

      openPdf(fileInBytes);
      postData.vehicleServiceType = ADDFileType.Xml;

      const xmlData = await requestPullVehicleTitleInfo(vin, id, postData, transactionId);
      if (xmlData) {
        handleXmlDataResponse(xmlData);
        const contactDetails = await requestContactDetails(params.id);
        if (contactDetails && xmlData.adD123Response) handleContactResponse(contactDetails, xmlData.adD123Response);
      }
    },
    [values, params.id]
  );

  const updateDiscrepancy = useCallback(() => {
    setDiscrepancies(true);
  }, []);

  const showAlertAndPostContactComment = useCallback((message: string, comment: string) => {
    setOpenAlertModal(true);
    setAlertMessage(message);
    postContactComment(comment);
  }, []);

  const handleContactResponse = useCallback((contactDetails: IContactDetailsResponse, adD123Response: IAdD123Response) => {
    if (!contactDetails.isMilitaryLendingActAvailable) {
      const { addresses } = contactDetails;
      const { titlingJurisdiction: registrationState } = adD123Response;
      if (addresses !== null && addresses[0]) {
        const { state: contactState } = addresses[0];
        if (contactState && registrationState && registrationState !== contactState) {
          const message = MESSAGE_REGISTRATION_STATE_DISCREPANCY;
          const comment = COMMENT_REGISTRATION_STATE_DISCREPANCY;
          setStateMismatchMessage((prevState) => [...prevState, message]);
          postContactComment(comment);
          updateDiscrepancy();
        }
      }
    }
  }, []);

  const handleAdD123Response = useCallback(
    (adD123Response: IAdD123Response) => {
      const { mileage } = values;
      const { brandPresent, brandReason, brandName, odometerReading, titlingJurisdiction } = adD123Response;
      if (brandPresent === BRAND_PRESENT) {
        const message = `${TITLE_BRAND}:( ${!brandReason ? brandName : brandReason} ) ${ERROR_MESSAGE_UNABLE_TO_PROCEED}`;
        const comment = `${TITLE_ADD123} ${TITLE_BRAND} - ${!brandReason ? brandName : brandReason}`;
        showAlertAndPostContactComment(message, comment);
        updateDiscrepancy();
      }
      if (odometerReading < 0) {
        const message = `${TITLE_ODOMETER_DISCREPANCY} ${ERROR_MESSAGE_PULL_MILEAGE}`;
        const comment = `${TITLE_ADD123} ${TITLE_DISCREPANCY} - ${ERROR_MESSAGE_PULL_MILEAGE}`;
        setStateMismatchMessage((prevState) => [...prevState, message]);
        postContactComment(comment);
        updateDiscrepancy();
      } else if (odometerReading > (mileage ?? 0)) {
        const message = `${TITLE_ODOMETER_DISCREPANCY}(${mileage})${ERROR_MESSAGE_MILEAGE}`;
        const comment = `${TITLE_ADD123} ${TITLE_DISCREPANCY} - ${TITLE_ODOMETER_READING}: ${mileage}`;
        setStateMismatchMessage((prevState) => [...prevState, message]);
        postContactComment(comment);
        updateDiscrepancy();
      }

      if (titlingJurisdiction) setFieldValue('assetRegistration.state', titlingJurisdiction);
      if (adD123Response) {
        const val = adD123Response;
        // Normalize branderState to always be string[]
        const branderState = val?.branderState;
        const normalizedBranderState: string[] = Array.isArray(branderState) ? branderState : branderState ? [branderState] : [];
        setFieldValue('isBrandedTitle', val?.brandedTitle);
        setFieldValue('assetBrand.brandedStateList', normalizedBranderState);
        setFieldValue('assetBrand.brandName', val?.brandName);
        setFieldValue('assetBrand.brandDescription', val?.brandDescription);
      }
    },
    [values]
  );

  const handleXmlDataResponse = useCallback((xmlData: IXmlDataResponse) => {
    if (xmlData.accessTokenDoesNotExists) {
      handleAccessTokenError(xmlData.authorizeUrl);
      return;
    }

    if (xmlData.isError) {
      Notistack.generalError(xmlData.errorDescription);
      return;
    }

    handleAdD123Response(xmlData.adD123Response);
  }, []);

  const handleAccessTokenError = useCallback((authorizeUrl: string) => {
    window.open(authorizeUrl, '_blank');
  }, []);

  const pullTitleInfo = async (overridePullConfirmation: CONFIRMATION_RESULT | CONFIRMATION_RESULT.PENDING) => {
    setOpenLpLeadOverrideModal(false);

    const { vin, id, make, year } = values;
    // Todo - add same enum on API side too to make it easy for all type of conformation popups
    if (overridePullConfirmation == CONFIRMATION_RESULT.CANCEL) {
      return;
    }
    let reuseData = undefined;
    switch (overridePullConfirmation) {
      case CONFIRMATION_RESULT.YES: {
        reuseData = false;
        break;
      }
    }

    const postData: IVehicleTitleInfoData = {
      stateCode: '',
      userId: userId ?? '',
      vehicleServiceType: ADDFileType.Pdf,
      caseNumber: '',
      make: make,
      year: year?.toString() ?? '',
      requesterCode: '',
      ncicCode: '',
      reusePulledDataConfirmed: reuseData,
    };

    if (vin && id) {
      setLoading(true);
      setIsTitleInfoPulled(false);
      const data = await requestPullVehicleTitleInfo(vin, id, postData);
      setLoading(false);
      if (data && data?.dataPulledInLast30Days && overridePullConfirmation == CONFIRMATION_RESULT.PENDING) {
        setOpenLpLeadOverrideModal(true);
        return;
      }

      if (data) {
        await handleDataResponse(data, postData);
        if (!data.isError && !data.accessTokenDoesNotExists) setIsTitleInfoPulled(true);
      }
    } else if (!id) {
      Notistack.generalError('Please select an asset!');
    } else if (!vin) {
      Notistack.generalError('Vin is required!');
    }
  };

  const removeMessageFromState = useCallback((message: string) => {
    setStateMismatchMessage((prevState) => prevState.filter((m) => m !== message));
  }, []);

  const postContactComment = useCallback(
    async (comment: string) => {
      await requestCreateContactComment({
        text: comment,
        contactId: params.id,
        commentType: COMMENT_TYPES.Comment,
      });
    },
    [params.id]
  );

  return (
    <>
      <AlertModal
        title={'Title Discrepancies:'}
        subtitle={alertMessage}
        buttonText={'Ok'}
        open={openAlertModal}
        hideCancel={true}
        onConfirm={() => setOpenAlertModal(false)}
        onClose={() => setOpenAlertModal(false)}
      />
      {stateMismatchMessage.map((message, index) => (
        <AlertModal
          key={index}
          title="Title Discrepancies:"
          subtitle={message}
          buttonText="Ok"
          open={true}
          hideCancel={true}
          onConfirm={() => removeMessageFromState(message)}
          onClose={() => removeMessageFromState(message)}
        />
      ))}
      <LoaderBackdrop
        open={loading}
        handleClose={() => setLoading(false)}
      />
      <FormPart
        name="Title"
        config={formConfigFields}
        marginTop={1}
        marginBottom={2}
      />
      {openLpLeadOverrideModal && (
        <Modal
          showFooter
          open
          buttonText="Override"
          cancelButtonText="Cancel"
          title="Requires LP Lead Override."
          dialogWidth="500px"
          hideSave={!hasOverrideRoles}
          onClose={() => pullTitleInfo(CONFIRMATION_RESULT.CANCEL)}
          onConfirm={() => pullTitleInfo(CONFIRMATION_RESULT.YES)}
        ></Modal>
      )}
    </>
  );
};
